/** REF: https://gist.github.com/jeengbe/4bc86f05a41a1831e6abf2369579cc7a */
//  (PII) - personally identifiable information

import { ReplayFrameEvent } from '@sentry/browser'
import { EventHint, ErrorEvent, Breadcrumb, BreadcrumbHint } from '@sentry/core'

// Returning true will not send the error to Sentry.
const shouldIgnoreError = (error: ErrorEvent, hint: EventHint): boolean => {
  return isNetworkError(error)
}

const typeErrorFetchFailedValues = new Set([
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'Load failed'
])

const apolloErrorIgnoreValues = new Set(['Load failed'])

const isNetworkError = (error: ErrorEvent): boolean => {
  const exception = error.exception?.values?.[0]

  if (
    exception?.type === 'TypeError' &&
    typeErrorFetchFailedValues.has(exception?.value || ('' as string))
  ) {
    return true
  }
  if (
    exception?.type === 'ApolloError' &&
    apolloErrorIgnoreValues.has(exception?.value || ('' as string))
  ) {
    return true
  }

  return false
}

// To keep compliant, we need to strip any PII data from the request and response data.
const stripPiiDataFromPayload = (event: ReplayFrameEvent) => {
  if (
    event.data.tag === 'performanceSpan' &&
    (event.data.payload.op === 'resource.fetch' ||
      event.data.payload.op === 'resource.xhr')
  ) {
    const tempEvent = event
    const payload = tempEvent.data.payload as any

    // Check if the payload has the expected structure before accessing
    if (payload.data?.request?.body && payload.data?.response?.body) {
      const { variables } = payload.data.request.body
      const { data } = payload.data.response.body

      payload.data.request.body.variables = findAndReplacePiiData(variables)
      payload.data.response.body.data = findAndReplacePiiData(data)
    }

    return tempEvent
  }
  return event
}

const piiList = [
  'firstName',
  'first_name',
  'lastName',
  'last_name',
  'email',
  'contactNum',
  'contact_num',
  'birthdate',
  'password',
  'passwordConfirmation',
  'currentPassword',
  'customer_details',
  'recipient_details',
  'address',
  'delivery_address',
  'line1',
  'line_1',
  'lineOne',
  'line2',
  'line_2',
  'lineTwo',
  'zip',
  'city',
  'country',
  'label'
]

/** Loop recursively through object, replacing any keys that match PII keys with a "*" */
const findAndReplacePiiData = (obj: any): any => {
  if (typeof obj === 'object' && obj !== null) {
    for (const keys in obj) {
      if (typeof obj[keys] === 'object' && obj[keys] !== null) {
        findAndReplacePiiData(obj[keys])
      } else {
        if (piiList.includes(keys)) {
          obj[keys] = '*'
        }
      }
    }
  }
  return obj
}

const addOperationNameToBreadcrumbs = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint
): Breadcrumb => {
  const operationName =
    hint?.input?.length > 1
      ? JSON.parse(hint?.input[1].body || {})?.operationName || ''
      : undefined
  if (operationName) {
    breadcrumb.data = {
      ...breadcrumb.data,
      operationName: operationName
    }
  }
  return breadcrumb
}

export {
  shouldIgnoreError,
  stripPiiDataFromPayload,
  addOperationNameToBreadcrumbs
}
